export type SettingsNavLink = {
  href: string;
  label: string;
  /** When false, page is a stub. Default true for wired surfaces. */
  implemented?: boolean;
};

export type SettingsNavGroup = {
  id: string;
  label: string;
  expandable: true;
  items: SettingsNavLink[];
};

export type SettingsNavEntry = SettingsNavLink | SettingsNavGroup;

export type SettingsNavSection = {
  id: string;
  label: string;
  items: SettingsNavEntry[];
};

export function isSettingsNavGroup(entry: SettingsNavEntry): entry is SettingsNavGroup {
  return "expandable" in entry && entry.expandable === true;
}

export const SETTINGS_GET_STARTED: SettingsNavLink = {
  href: "/settings/getting-started",
  label: "Get started",
  implemented: true,
};

export const SETTINGS_NAV_SECTIONS: SettingsNavSection[] = [
  {
    id: "personal",
    label: "Personal settings",
    items: [
      { href: "/settings/account", label: "Profile", implemented: true },
      { href: "/settings/account/mailboxes", label: "Mailboxes & domains", implemented: true },
      { href: "/settings/account/voices", label: "Phone numbers", implemented: true },
      { href: "/settings/notifications", label: "Notifications", implemented: true },
      { href: "/settings/chrome-extension", label: "Chrome extension", implemented: true },
      { href: "/settings/conversations", label: "Conversations", implemented: true },
    ],
  },
  {
    id: "workspace",
    label: "Workspace settings",
    items: [
      { href: "/settings/workspace-overview", label: "Workspace overview", implemented: true },
      {
        id: "deliverability",
        label: "Deliverability suite",
        expandable: true,
        items: [
          { href: "/settings/deliverability-suite/overview", label: "Overview", implemented: true },
          { href: "/settings/deliverability-suite/domains", label: "Domains", implemented: true },
          { href: "/settings/deliverability-suite/mailboxes", label: "Mailboxes", implemented: true },
          {
            href: "/settings/deliverability-suite/bounce-logs",
            label: "Bounce logs",
            implemented: true,
          },
        ],
      },
      {
        id: "users-teams",
        label: "Users and teams",
        expandable: true,
        items: [
          { href: "/settings/users", label: "Users", implemented: true },
          { href: "/settings/teams", label: "Teams", implemented: true },
          {
            href: "/settings/permission-profiles",
            label: "Permission profiles",
            implemented: true,
          },
        ],
      },
      {
        id: "billing",
        label: "Billing and credits",
        expandable: true,
        items: [
          { href: "/settings/plans", label: "Plan overview", implemented: true },
          { href: "/settings/features-add-ons", label: "Product Add-ons", implemented: true },
          {
            href: "/settings/plans/license-settings",
            label: "License settings",
            implemented: true,
          },
          { href: "/settings/credits", label: "Credits & AI usage", implemented: true },
          { href: "/settings/ai-runs", label: "AI run usage", implemented: true },
        ],
      },
      {
        id: "integrations",
        label: "Integrations",
        expandable: true,
        items: [
          { href: "/settings/integrations", label: "Connected Integrations", implemented: true },
          { href: "/settings/integrations/mcp", label: "MCP", implemented: true },
        ],
      },
      {
        id: "icp",
        label: "Ideal customer profile",
        expandable: true,
        items: [
          { href: "/settings/personas", label: "Personas", implemented: true },
          { href: "/settings/intent-data", label: "Buying intent", implemented: true },
          { href: "/settings/websites", label: "Website visitors", implemented: true },
          { href: "/settings/signals", label: "Signals", implemented: true },
          { href: "/settings/scoring-models", label: "Scoring", implemented: true },
        ],
      },
      { href: "/settings/content-center", label: "AI context center", implemented: true },
      { href: "/settings/sequence", label: "Prompt templates", implemented: true },
      {
        id: "rules",
        label: "Rules of engagement",
        expandable: true,
        items: [{ href: "/settings/org", label: "Org config", implemented: true }],
      },
      {
        id: "team-email",
        label: "Team email & sequences",
        expandable: true,
        items: [
          { href: "/settings/snippets", label: "Snippets", implemented: true },
          { href: "/settings/tracking-subdomains", label: "Tracking", implemented: true },
        ],
      },
      {
        id: "team-dialer",
        label: "Team dialer",
        expandable: true,
        items: [
          { href: "/settings/calls", label: "Overview", implemented: true },
          {
            href: "/settings/calls/recording",
            label: "Recording",
            implemented: true,
          },
          {
            href: "/settings/calls/purposes",
            label: "Purposes",
            implemented: true,
          },
          {
            href: "/settings/calls/dispositions",
            label: "Dispositions",
            implemented: true,
          },
          {
            href: "/settings/calls/voicemail-drops",
            label: "Voicemail drops",
            implemented: true,
          },
          {
            href: "/settings/calls/scorecards",
            label: "Scorecards",
            implemented: true,
          },
        ],
      },
      {
        id: "team-conversations",
        label: "Team conversations",
        expandable: true,
        items: [
          {
            href: "/settings/team-conversations",
            label: "Recording configuration",
            implemented: true,
          },
          {
            href: "/settings/team-conversations/users",
            label: "Team permissions",
            implemented: true,
          },
          {
            href: "/settings/team-conversations/trackers",
            label: "Trackers",
            implemented: true,
          },
          {
            href: "/settings/team-conversations/scorecards",
            label: "Scorecards",
            implemented: true,
          },
          {
            href: "/settings/team-conversations/custom-field-prompts",
            label: "Custom field prompts",
            implemented: true,
          },
        ],
      },
      { href: "/settings/team-meetings", label: "Team meetings", implemented: true },
      { href: "/settings/defaults/people", label: "Team sharing & defaults", implemented: true },
      {
        id: "system-activity",
        label: "System activity",
        expandable: true,
        items: [
          { href: "/settings/data-requests", label: "Data requests", implemented: true },
          { href: "/settings/activity-log", label: "System activity log", implemented: true },
        ],
      },
    ],
  },
  {
    id: "data",
    label: "Data management",
    items: [
      {
        id: "objects-fields",
        label: "Objects, fields, stages",
        expandable: true,
        items: [
          { href: "/settings/leads", label: "Lead fields & stages", implemented: true },
          { href: "/settings/accounts", label: "Account fields & stages", implemented: true },
          { href: "/settings/opportunities", label: "Deal fields & stages", implemented: true },
        ],
      },
      {
        id: "imports-exports",
        label: "Imports and exports",
        expandable: true,
        items: [
          {
            href: "/settings/imports-and-exports/leads",
            label: "Lead import",
            implemented: true,
          },
          {
            href: "/settings/imports-and-exports/accounts",
            label: "Account import",
            implemented: true,
          },
          {
            href: "/settings/imports-and-exports/exports",
            label: "CSV Exports",
            implemented: true,
          },
          {
            href: "/settings/imports-and-exports/enriched-csv",
            label: "Enriched CSVs",
            implemented: true,
          },
          {
            href: "/settings/imports-and-exports/csv-export-settings",
            label: "CSV Export Settings",
            implemented: true,
          },
        ],
      },
      {
        id: "recycling-bin",
        label: "Recycling bin",
        expandable: true,
        items: [
          {
            href: "/settings/recycling-bin/accounts",
            label: "Accounts",
            implemented: true,
          },
          {
            href: "/settings/recycling-bin/leads",
            label: "Leads",
            implemented: true,
          },
        ],
      },
      { href: "/settings/removal-request", label: "Removal requests", implemented: true },
    ],
  },
];

const EXPANDED_KEY = "sales-ops.settings.nav.expanded";

export function flattenSettingsLinks(): SettingsNavLink[] {
  const out: SettingsNavLink[] = [SETTINGS_GET_STARTED];
  for (const section of SETTINGS_NAV_SECTIONS) {
    for (const entry of section.items) {
      if (isSettingsNavGroup(entry)) out.push(...entry.items);
      else out.push(entry);
    }
  }
  return out;
}

export function isSettingsActivePath(href: string, pathname: string): boolean {
  if (href === "/settings/account") {
    return pathname === "/settings/account" || pathname === "/settings/profile";
  }
  if (href === "/settings/integrations") {
    return pathname === "/settings/integrations";
  }
  if (href === "/settings/sequence") {
    return pathname === "/settings/sequence" || pathname.startsWith("/settings/prompts");
  }
  if (href === "/settings/signals") {
    return pathname === "/settings/signals" || pathname.startsWith("/settings/providers");
  }
  if (href === "/settings/integrations/mcp") {
    return (
      pathname === "/settings/integrations/mcp" ||
      pathname.startsWith("/settings/plugins")
    );
  }
  if (href === "/settings/credits") {
    return pathname === "/settings/credits" || pathname === "/settings/ai";
  }
  if (href === "/settings/users") {
    return pathname === "/settings/users";
  }
  if (pathname === href) return true;
  if (pathname.startsWith(`${href}/`)) {
    return href.split("/").length < pathname.split("/").length;
  }
  return false;
}

export function groupContainsPath(group: SettingsNavGroup, pathname: string): boolean {
  return group.items.some((item) => isSettingsActivePath(item.href, pathname));
}

export function groupIdsForPath(pathname: string): string[] {
  const ids: string[] = [];
  for (const section of SETTINGS_NAV_SECTIONS) {
    for (const entry of section.items) {
      if (isSettingsNavGroup(entry) && groupContainsPath(entry, pathname)) {
        ids.push(entry.id);
      }
    }
  }
  return ids;
}

export function loadExpandedSettingsGroups(pathname: string): Set<string> {
  let stored: string[] = [];
  try {
    const raw = localStorage.getItem(EXPANDED_KEY);
    if (raw) stored = JSON.parse(raw) as string[];
  } catch {
    /* ignore */
  }
  const next = new Set(stored);
  for (const id of groupIdsForPath(pathname)) next.add(id);
  if (next.size === 0) {
    for (const section of SETTINGS_NAV_SECTIONS) {
      for (const entry of section.items) {
        if (isSettingsNavGroup(entry)) next.add(entry.id);
      }
    }
  }
  return next;
}

export function persistExpandedSettingsGroups(ids: Set<string>): void {
  try {
    localStorage.setItem(EXPANDED_KEY, JSON.stringify([...ids]));
  } catch {
    /* ignore */
  }
}

export function filterSettingsSections(query: string): SettingsNavSection[] {
  const q = query.trim().toLowerCase();
  if (!q) return SETTINGS_NAV_SECTIONS;

  return SETTINGS_NAV_SECTIONS.map((section) => {
    const items: SettingsNavEntry[] = [];
    for (const entry of section.items) {
      if (isSettingsNavGroup(entry)) {
        const matchedChildren = entry.items.filter((i) =>
          i.label.toLowerCase().includes(q),
        );
        if (entry.label.toLowerCase().includes(q)) {
          items.push(entry);
        } else if (matchedChildren.length > 0) {
          items.push({ ...entry, items: matchedChildren });
        }
      } else if (entry.label.toLowerCase().includes(q)) {
        items.push(entry);
      }
    }
    return { ...section, items };
  }).filter((s) => s.items.length > 0);
}
